import { TRAINING_GATE } from '../../constants';
import { canTrainChild, minAnnotationsOf } from '../../objectTree';

export const ROOT_PATH_LABEL = 'Root level';

/**
 * Ancestor names of `object`, root first, joined for display ("Tree › Conifer").
 * Walks `parent_uuid` through the flat `objects` list; a cycle or a missing
 * parent simply ends the walk.
 */
export const buildObjectPath = (object, objects = []) => {
	const byUuid = new Map(objects.map(node => [node.uuid, node]));
	const names = [];
	const seen = new Set();
	let parent = byUuid.get(object?.parent_uuid);
	while (parent && !seen.has(parent.uuid)) {
		seen.add(parent.uuid);
		names.unshift(parent.name);
		parent = byUuid.get(parent.parent_uuid);
	}
	return names.length ? names.join(' › ') : ROOT_PATH_LABEL;
};

/** Annotation figures for display: `1,840`. */
export const formatCount = value => Number(value ?? 0).toLocaleString();

/**
 * What the training gate should show for `object`: the count against the
 * node's threshold, and whether that is enough. The threshold and the rule
 * are the tree helpers' (`minAnnotationsOf`, `canTrainChild`), so the card
 * pill and the gate can never disagree.
 *
 * @returns {{ status: string, annotationCount: number, target: number, missing: number, percent: number }}
 */
export const getTrainingGate = object => {
	const annotationCount = Math.max(0, Number(object?.annotations) || 0);
	const target = Math.max(0, Number(minAnnotationsOf(object)) || 0);
	const missing = Math.max(0, target - annotationCount);
	const percent = target > 0 ? Math.min(100, Math.round((annotationCount / target) * 100)) : 100;
	const status = canTrainChild(object) ? TRAINING_GATE.READY : TRAINING_GATE.LOCKED;

	return { status, annotationCount, target, missing, percent };
};

// POST /taxonomy/nodes caps `key` at 64 characters.
export const MAX_OBJECT_KEY_LENGTH = 64;
const KEY_SUFFIX_LENGTH = 5;

/**
 * Derive a taxonomy key from a display name, in the shape the API requires:
 * lower-case letters and digits with a single `_` between words, at most 64
 * characters. A short random suffix keeps two objects with the same name apart.
 */
export const buildObjectKey = name => {
	const suffix = Math.random()
		.toString(36)
		.slice(2, 2 + KEY_SUFFIX_LENGTH);
	const slug = String(name ?? '')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '_')
		.replace(/^_+|_+$/g, '')
		.slice(0, MAX_OBJECT_KEY_LENGTH - KEY_SUFFIX_LENGTH - 1)
		.replace(/_+$/, '');
	return slug ? `${slug}_${suffix}` : suffix;
};
